import { Modal } from '../../../../components/Modal/Modal'
import React, { useEffect, useState } from 'react'
import { Col, Container, Form, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { SelectDropdown, SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { useGetIntegrationUsersQuery, useLazyGetIntegrationUsersQuery, useSaveResultMutation } from '../../../../store/integrations/integrations'
import { AIFeature, ComplexAIResultType } from '@zocks-communications/client-sdk-js'
import { useGetRoomSessionQuery } from '../../../../store/media/media'
import {
  AIResultDisplayModel,
  AIResultViewModel,
  SegmentedResultViewModel,
  StructuredSummaryResultViewModel,
} from '../../../../apps/professional-app/fa/AIResultMapper'
import { useGetFeatureByIdQuery } from '../../../../store/ai/ai'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { WealthboxIntegrationRow } from '../../../ProfileSettings/Integrations/Wealthbox/WealthboxIntegrationRow'
import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../../../common/errorMessages'
import { FormFeedback } from '../../../../components/Form/FormFeedback'
import { ActionableAIResultProps } from '../../../../apps/professional-app/fa/models'
import { ResultViewer } from '../../../../apps/professional-app/fa/ResultViewer'
import { ActionableAIResultGroup } from '../../../../apps/professional-app/fa/AIResultsViews'
import { updateAIResultsProperties } from '../../../../apps/professional-app/fa/OndemandAIExecution'
import { ContactFormSelectDropdown } from '../ContactDropdown'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import './AddResultToCRMModal.scss'
import { SalesforceIntegrationRow } from '../../../ProfileSettings/Integrations/Salesforce/SalesforceIntegrationRow'
import { addToast } from '../../../../store/toast/toastSlice'
import { RoomSession } from '../../../../models/Room'
import { getContactOptions } from '../utils'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig } from '../../../../models/Account'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]

export interface AddTaskToCRMModalProps {
  roomId: string
  sessionId: string
  aiFeatureId: string
  aiResults: AIResultViewModel[]
  show: boolean
  onHide: () => void
  serviceId: string
}

type FormValues = {
  externalUserId: string
  dueDate: Date
  priority: string
}

export const AddTaskToCRMModal: React.FC<AddTaskToCRMModalProps> = ({ show, onHide, roomId, sessionId, aiFeatureId, aiResults: _aiResults, serviceId }) => {
  const _useAppDispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [aiResults, setAIResults] = useState(_aiResults)

  const { tracked } = useTracking()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { data: roomSession, isFetching: isRoomSessionFetching } = useGetRoomSessionQuery({ accountId, roomId, sessionId }, { refetchOnMountOrArgChange: true })
  const [triggerSaveResults, { isSuccess, isLoading }] = useSaveResultMutation()
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const defaultValues = {
    externalUserId: '',
    dueDate: new Date(),
    priority: 'Medium',
  }

  const handleOnHide = () => {
    const displayModels = getDisplayModels(aiResults)
    const results = displayModels.reduce((previousValue, currentValue) => {
      return updateAIResultsProperties({ aiResult: currentValue, aiResults: previousValue, propertyToUpdate: 'selected', propertyValue: false })
    }, aiResults)
    setAIResults(results)
    onHide()
  }

  const onSelectTaskAIResult = (checked: boolean, aiResultDisplayModel: AIResultDisplayModel) => {
    const results = updateAIResultsProperties({ aiResult: aiResultDisplayModel, aiResults, propertyToUpdate: 'selected' })
    setAIResults(results)
  }

  const onSelectAll = (checked: boolean) => {
    const displayModels = getDisplayModels(aiResults)
    const results = displayModels.reduce((previousValue, currentValue) => {
      return updateAIResultsProperties({ aiResult: currentValue, aiResults: previousValue, propertyToUpdate: 'selected', propertyValue: checked })
    }, aiResults)
    setAIResults(results)
  }

  const handleSave = tracked(
    (data: FormValues) => {
      const selectedDisplayModels = getDisplayModels(aiResults).filter((displayModel) => displayModel.selected)
      const serviceIntegration = getAccountCRMIntegrationConfig(account?.data, serviceId)
      const contactMapping = serviceIntegration?.mappings[AccountIntegrationConfigMappingType.CONTACT]

      triggerSaveResults({
        accountId,
        connector: serviceId,
        userId: currentUserId,
        body: {
          roomId: roomId,
          sessionId: sessionId,
          aiFeatureId: aiFeatureId,
          externalUserIds: [data.externalUserId],
          extra: {
            ...(contactMapping && { contact_type: contactMapping }),
            results: selectedDisplayModels.map((m) => {
              return m.result.toString()
            }),
            dueDate: data.dueDate.toISOString(),
            priority: data.priority,
          },
        },
      })
    },
    { eventName: TrackEvent.SAVE_TASKS_TO_CRM },
    {
      eventDetailsExtractor: (data: FormValues) => {
        return {
          roomId: roomId,
          sessionId: sessionId,
          externalUserId: data.externalUserId,
          connector: serviceId,
        }
      },
    },
  )

  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit } = form

  useEffect(() => {
    if (isSuccess) {
      handleOnHide()
      form.reset()
      _useAppDispatch(addToast({ variant: 'success', title: 'Results saved', messages: ['Results were successfully saved to your CRM'] }))
    }
  }, [form, isSuccess, onHide, _useAppDispatch])

  return (
    <Modal
      size='xl'
      show={show}
      onHide={handleOnHide}
      header={
        <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-center'>
          Save your Tasks to&nbsp;<span className='text-capitalize'>{serviceId}</span>
        </BootstrapModal.Title>
      }
      body={
        <FormProvider {...form}>
          {!isRoomSessionFetching && roomSession ? (
            <AddTaskToCRMModalBody
              aiFeatureId={aiFeatureId}
              aiResults={aiResults}
              roomSession={roomSession.data}
              onSelectTaskAIResult={onSelectTaskAIResult}
              onSelectAll={onSelectAll}
              serviceId={serviceId}
            />
          ) : (
            <div className='d-flex justify-content-center'>
              <Spinner />
            </div>
          )}
        </FormProvider>
      }
      showFooterActions={true}
      onFooterPrimaryAction={handleSubmit(handleSave)}
      footerPrimaryActionLoading={isLoading}
      onFooterSecondaryAction={handleOnHide}
      centered={true}
    />
  )
}

const AddTaskToCRMModalBody: React.FC<{
  roomSession: RoomSession
  aiFeatureId: string
  aiResults: AIResultViewModel[]
  onSelectTaskAIResult: (checked: boolean, aiResult: AIResultDisplayModel) => void
  onSelectAll: (checked: boolean) => void
  serviceId: string
}> = ({ roomSession, aiFeatureId, aiResults, onSelectTaskAIResult, onSelectAll, serviceId }) => {
  const accountId = useAppSelector(selectAccountId)
  const { control, getValues, setValue } = useFormContext<FormValues>()
  const { data: aiFeature, isFetching: isFeatureFetching } = useGetFeatureByIdQuery({
    accountId: accountId,
    aiFeatureId: aiFeatureId,
  })
  const {
    data: contacts,
    isFetching: isContactsFetching,
    isError: isContactsError,
    error: contactsError,
  } = useGetIntegrationUsersQuery({ accountId, connector: serviceId })

  const [triggerGetContacts, { isError: isTriggerGetContactsError, error: triggerContactsError }] = useLazyGetIntegrationUsersQuery()
  const [contactOptions, setContactOptions] = useState<SelectOption[]>([])

  const priorityOptions = [
    { label: 'Low', id: 'Low' },
    { label: 'Medium', id: 'Medium' },
    { label: 'High', id: 'High' },
  ]

  useEffect(() => {
    if (contacts && contacts.data) {
      setContactOptions(getContactOptions(contacts.data, serviceId))
    }
  }, [contacts])

  const onSearchInputChange = async (searchInput: string) => {
    try {
      const contactsData = await triggerGetContacts({ accountId, connector: serviceId, filters: { name: searchInput } }).unwrap()
      if (contactsData && contactsData.data) {
        setContactOptions(getContactOptions(contactsData.data, serviceId))
      }
    } catch (e) {
      console.error(e)
    }
  }

  if ((isContactsFetching && !contacts) || isFeatureFetching) {
    return (
      <div className='d-flex justify-content-center'>
        <Spinner />
      </div>
    )
  }

  const getConnectorIntegrationRow = () => {
    if (serviceId === 'wealthbox') {
      return <WealthboxIntegrationRow useIntegrationRedirectUri />
    }
    if (serviceId === 'salesforce') {
      return <SalesforceIntegrationRow useIntegrationRedirectUri />
    }
  }

  if (
    (isContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(contactsError).match(ignoredMessage))) ||
    (isTriggerGetContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(triggerContactsError).match(ignoredMessage)))
  ) {
    return (
      <>
        <Container className='p-0'>
          <Row>
            <Col>Your integration is invalid, please reconnect your integration:</Col>
          </Row>
        </Container>
        {getConnectorIntegrationRow()}
      </>
    )
  }
  const isAllSelected = (): boolean => {
    const displayModels = getDisplayModels(aiResults)
    return displayModels.every((aiResult) => aiResult.selected) || false
  }

  return (
    <Container>
      <Row className='mt-3'>
        <Col xs={6}>
          <ContactFormSelectDropdown
            fieldName='externalUserId'
            contactOptions={contactOptions}
            serviceId={serviceId}
            onSearchInputChange={onSearchInputChange}
          />
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col xs={6}>
          <div className='label-medium'>Due date</div>
          <Controller
            name='dueDate'
            control={control}
            rules={{ required: 'Due date is required.' }}
            render={({ field, fieldState }) => (
              <>
                <DatePicker className='form-control' selected={field.value} onChange={(date) => field.onChange(date)} todayButton='Today' />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col xs={6}>
          <div className='label-medium'>Priority</div>
          <Controller
            name='priority'
            control={control}
            render={({ field, fieldState }) => (
              <>
                <SelectDropdown
                  initialSelectedOption={priorityOptions.find((option) => option.id === field.value)}
                  options={priorityOptions}
                  className='py-2 zocks-px-0.75'
                  onOptionSelected={(option) => field.onChange(option.id)}
                  placeholder='Select your priority'
                />
              </>
            )}
          />
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <div className='label-medium'>Tasks</div>
        </Col>
      </Row>
      <Row>
        <Col xs='auto'>
          <Form.Check
            id='select-all-task-checkbox'
            className='form-check'
            checked={isAllSelected()}
            onChange={(e) => onSelectAll(e.currentTarget.checked)}
            label='Select all'
          />
        </Col>
      </Row>
      <Row>
        <Col>
          {aiFeature ? (
            aiResults.map((aiResultViewModel) => {
              return (
                <ActionableAIResultGroup
                  key={`${aiResultViewModel.id}-airesult-group`}
                  aiFeature={aiFeature.data as AIFeature}
                  aiResult={aiResultViewModel}
                  ActionableAIResultElement={SelectableAIResultTask}
                  showAIResultEdit={true}
                  showScoreAIResult={false}
                  onSelectTaskAIResult={onSelectTaskAIResult}
                />
              )
            })
          ) : (
            <Spinner />
          )}
        </Col>
      </Row>
    </Container>
  )
}

const SelectableAIResultTask: React.FC<ActionableAIResultProps> = ({ aiFeature, aiResult, aiResultDisplay, onAIResultChanged, onSelectTaskAIResult }) => {
  return <ResultViewer aiFeature={aiFeature} aiResult={aiResultDisplay} onAIResultChanged={onAIResultChanged} onSelectTaskAIResult={onSelectTaskAIResult} />
}

const getLeafAIResultDisplayModels = (result: AIResultDisplayModel | AIResultDisplayModel[]): AIResultDisplayModel[] => {
  if (Array.isArray(result)) {
    return result
      .filter((r) => !r.dismissed)
      .map((r) => {
        return getLeafAIResultDisplayModels(r)
      })
      .flat()
  }

  if (typeof result.result === 'object') {
    return Object.entries(result.result)
      .map(([key, value]) => {
        return getLeafAIResultDisplayModels(value)
      })
      .flat()
  }

  return [result]
}

const getDisplayModels = (aiResultViewModels: AIResultViewModel[]) => {
  return aiResultViewModels
    .map((aiResult) => {
      if (aiResult.result && aiResult.result.type === ComplexAIResultType.SEGMENTED) {
        const typedAIResult = aiResult.result as SegmentedResultViewModel
        return typedAIResult.segmentResults
          .map((segmentResult) => {
            return segmentResult.results.map((r) => getLeafAIResultDisplayModels(r.result)).flat()
          })
          .flat()
      } else {
        const typedAIResult = aiResult.result as StructuredSummaryResultViewModel
        return typedAIResult.result
          .map((r) => {
            return getLeafAIResultDisplayModels(r).flat()
          })
          .flat()
      }
    })
    .flat()
}
